// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { getConfig, ImprHeaders } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { updateWorkflowId } from '@tracing/index';
import { generateWorkflowId, getWorkflowIdFromUrl } from '@utils/WorkflowIdHelper';

class ApiClient {
  baseUrl: string;
  constructor(basePath: string, version: number) {
    const env = import.meta.env;
    const config = getConfig(env);
    const localhostRegex = /localhost/;

    if (localhostRegex.test(config.API_URL)) {
      this.baseUrl = 'https://localhost:5000/api/v1/';
    } else {
      this.baseUrl = `${config.API_URL}/${basePath}/api/v${version}/`;
    }
  }

  async request<TResponse>(url: string, options: RequestInit) {
    const fullUrl = `${this.baseUrl}${url}`;
    return fetch(fullUrl, options).then(async (response) => {
      // Check if there is a response body
      const contentLength = response.headers.get('content-length');
      const isNoContent = response.status === 204 || contentLength === '0';
      const text = await response.text();
      if (!text || isNoContent) {
        // No response body (e.g., TResponse is void)
        if (response.ok) {
          return undefined as TResponse;
        }
        return Promise.reject(undefined);
      }
      try {
        const json = JSON.parse(text);
        return response.ok ? (json as TResponse) : Promise.reject(json);
      } catch {
        // If response is not valid JSON, return as text
        return response.ok ? (text as unknown as TResponse) : Promise.reject(text);
      }
    });
  }

  get<TResponse>(url: string, codingContext?: CodingContext | undefined): Promise<TResponse> {
    return this.request(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(codingContext),
    });
  }

  post<TResponse>(
    url: string,
    requestBuilder: (codingContext: CodingContext) => unknown,
    codingContext: CodingContext | undefined,
  ): Promise<TResponse> {
    if (!codingContext) {
      throw new Error('Coding context is not initialized');
    }

    return this.request(url, {
      method: 'POST',
      headers: this.getHeaders(codingContext),
      credentials: 'include',
      body: JSON.stringify(requestBuilder(codingContext)),
    });
  }

  private getHeaders(codingContext: CodingContext | undefined): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (codingContext) {
      headers[ImprHeaders.ImprCodingCtx] = codingContext.buildImprCodingCtxHeader();
    }

    // Retrieve workflow ID from URL or generate a new one
    const workflowId = getWorkflowIdFromUrl() || generateWorkflowId();
    headers[ImprHeaders.ImprWorkflowId] = workflowId;
    updateWorkflowId(workflowId);
    return headers;
  }
}

export const apiClient = new ApiClient('pa-patient-web', 1);
